import * as React from "react";
import AppBar from "@mui/material/AppBar";
import Box from "@mui/material/Box";
import Toolbar from "@mui/material/Toolbar";
import Typography from "@mui/material/Typography";
import IconButton from "@mui/material/IconButton";
import MenuIcon from "@mui/icons-material/Menu";
import AccountCircle from "@mui/icons-material/AccountCircle";
import MenuItem from "@mui/material/MenuItem";
import Menu from "@mui/material/Menu";
import { signOut } from "firebase/auth";
import { useNavigate, useLocation } from "react-router-dom";
import { auth } from "../Config/firebase";
import { useDeviceType } from "../Utils/useDeviceType";
import { Home, Apartment, ArrowBack, Security } from "@mui/icons-material";
import { useContext } from "react";
import { AuthContext } from "../AppContext/AuthContext";
import { Avatar, Tooltip } from "@mui/material";
import { getRouteConfig } from "./routeConfig";
import NotificationBell from "../Shared/NotificationBell";
import { PermissionModeContext } from "../Shared/PermissionMode";

export default function MenuAppBar({ toggleDrawer }) {
  const [anchorEl, setAnchorEl] = React.useState(null);
  const navigate = useNavigate();
  const location = useLocation();
  const { isMobile } = useDeviceType();
  const { userInfo, currentUser } = useContext(AuthContext);
  const { active, setActive, isAvailable } = useContext(PermissionModeContext);

  const routeInfo = getRouteConfig(location.pathname);
  const pageTitle = routeInfo?.dynamicLabel || routeInfo?.label || "";
  const hasParent = routeInfo?.parent;

  const handleMenu = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const userLogout = async () => {
    await signOut(auth);
    setTimeout(() => {
      navigate("/signin");
    }, 500);
  };

  const displayName = currentUser?.displayName || userInfo?.displayName || "";
  const initials = displayName
    ? displayName.split(" ").map((n) => n[0]).join("").substring(0, 2).toUpperCase()
    : "";

  return (
    <Box>
      <AppBar
        position="static"
        elevation={1}
        sx={{
          background: "linear-gradient(90deg, #1a237e 0%, #283593 100%)",
        }}
      >
        <Toolbar>
          {/* Back button on sub-pages (mobile) or Menu button */}
          {isMobile && hasParent ? (
            <IconButton
              size="large"
              edge="start"
              color="inherit"
              aria-label="back"
              onClick={() => navigate(hasParent)}
              sx={{ mr: 1 }}
            >
              <ArrowBack />
            </IconButton>
          ) : (
            <IconButton
              size="large"
              edge="start"
              color="inherit"
              aria-label="menu"
              onClick={() => {
                if (isMobile) {
                  navigate("/");
                } else {
                  toggleDrawer(true)({});
                }
              }}
              sx={{ mr: 1 }}
            >
              {isMobile ? <Home /> : <MenuIcon />}
            </IconButton>
          )}

          {/* Community Name + Page Title */}
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexGrow: 1, minWidth: 0 }}>
            {!isMobile && <Apartment sx={{ fontSize: 24, opacity: 0.9 }} />}
            <Typography variant="h6" component="div" fontWeight="bold" noWrap>
              {isMobile && pageTitle ? pageTitle : (userInfo?.communityName || "Community")}
            </Typography>
          </Box>

          {/* Notifications */}
          <NotificationBell />

          {/* Permission Mode Toggle */}
          {isAvailable && (
            <Tooltip title={active ? "Exit Permission Mode" : "Permission Mode"}>
              <IconButton
                color="inherit"
                onClick={() => setActive(!active)}
                data-permission-mode="true"
                sx={{
                  ml: 0.5,
                  bgcolor: active ? "rgba(76,175,80,0.3)" : "transparent",
                  "&:hover": { bgcolor: active ? "rgba(76,175,80,0.4)" : "rgba(255,255,255,0.1)" },
                }}
              >
                <Security fontSize="small" />
              </IconButton>
            </Tooltip>
          )}

          {/* Profile */}
          <IconButton
            size="large"
            aria-label="account"
            aria-controls="menu-appbar"
            aria-haspopup="true"
            onClick={handleMenu}
            color="inherit"
          >
            <Avatar
              src={currentUser?.photoURL}
              sx={{ width: 32, height: 32, bgcolor: "rgba(255,255,255,0.2)", fontSize: "0.85rem" }}
            >
              {initials}
            </Avatar>
          </IconButton>
          <Menu
            id="menu-appbar"
            anchorEl={anchorEl}
            anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
            keepMounted
            transformOrigin={{ vertical: "top", horizontal: "right" }}
            open={Boolean(anchorEl)}
            onClose={handleClose}
          >
            {displayName && (
              <MenuItem disabled>
                <Typography variant="body2" color="text.secondary">
                  {displayName}
                </Typography>
              </MenuItem>
            )}
            <MenuItem onClick={() => { handleClose(); navigate("/profile"); }}>My Profile</MenuItem>
            <MenuItem onClick={() => { handleClose(); navigate("/notification-settings"); }}>Notification Settings</MenuItem>
            <MenuItem onClick={userLogout}>Logout</MenuItem>
          </Menu>
        </Toolbar>
      </AppBar>
    </Box>
  );
}
